import { MenuItem, Select } from "@mui/material";
import MDBox from "components/MDBox";
import UiInput from "components/base/UiInput";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { Controller, FieldPath, useFormContext } from "react-hook-form";
import { WorksheetItemInput } from "DDD/action-objects/WorksheetItemUpdate";
import { compactSelectSx } from "./worksheetStyles";

export type WorksheetFieldOption = { value: string; label: string };

const labelSx = {
  color: draftColors.textLabel,
  lineHeight: "12px",
  minHeight: "12px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

type WorksheetFieldProps = {
  name: string;
  label: string;
  isReadOnly?: boolean;
};

export function WorksheetInputField({
  name,
  label,
  placeholder,
  isReadOnly,
  textAlign = "right",
}: WorksheetFieldProps & { placeholder?: string; textAlign?: "left" | "right" }) {
  const { control } = useFormContext<WorksheetItemInput>();

  return (
    <MDBox display="flex" flexDirection="column" gap="4px" minWidth={0}>
      <UiTypography uiVariant="label-xs" sx={labelSx} title={label}>
        {label}
      </UiTypography>
      <Controller
        control={control}
        name={name as FieldPath<WorksheetItemInput>}
        render={({ field }) => (
          <UiInput
            value={(field.value as string) ?? ""}
            readOnly={isReadOnly}
            textAlign={textAlign}
            placeholder={placeholder}
            fullWidth
            onChange={(event) => field.onChange(event.target.value)}
          />
        )}
      />
    </MDBox>
  );
}

export function WorksheetSelectField({
  name,
  label,
  options,
  isReadOnly,
  fallback,
  allowEmpty = false,
  onValueChange,
}: WorksheetFieldProps & {
  options: WorksheetFieldOption[];
  fallback?: string;
  allowEmpty?: boolean;
  onValueChange?: (value: string) => void;
}) {
  const { control } = useFormContext<WorksheetItemInput>();

  return (
    <MDBox display="flex" flexDirection="column" gap="4px" minWidth={0}>
      <UiTypography uiVariant="label-xs" sx={labelSx} title={label}>
        {label}
      </UiTypography>
      <Controller
        control={control}
        name={name as FieldPath<WorksheetItemInput>}
        render={({ field }) => (
          <Select
            value={
              allowEmpty
                ? (field.value as string) || ""
                : (field.value as string) || fallback || options[0]?.value || ""
            }
            displayEmpty={allowEmpty}
            disabled={isReadOnly}
            sx={{ ...compactSelectSx, display: "flex", width: "100%" }}
            onChange={(event) => {
              field.onChange(event.target.value);
              onValueChange?.(event.target.value);
            }}
          >
            {options.map((option) => (
              <MenuItem key={option.value} value={option.value} sx={{ fontSize: "11px" }}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        )}
      />
    </MDBox>
  );
}

export function WorksheetReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <MDBox display="flex" flexDirection="column" gap="4px" minWidth={0}>
      <UiTypography uiVariant="label-xs" sx={labelSx} title={label}>
        {label}
      </UiTypography>
      <UiInput value={value} readOnly fullWidth />
    </MDBox>
  );
}
